import React from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface AuthCheckScreenProps {
  /** 認証確認が長引いていることを利用者に伝えるか */
  isSlow?: boolean
  /** 失敗の説明。渡されたときは待機表示の代わりに再試行を出す */
  error?: string | null
  onRetry?: () => void
}

/**
 * 認証確認の待機と失敗をひとつにまとめた全画面表示。
 *
 * AuthGuard / AdminGuard / LandingRoute が同じ表示を出すため、ここに寄せている。
 * 実測できない進捗率や残り秒数は出さない（PRI-251）。
 */
export function AuthCheckScreen({
  isSlow = false,
  error = null,
  onRetry,
}: AuthCheckScreenProps): React.JSX.Element {
  if (error) {
    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
      >
        <p className="max-w-md text-gray-700">{error}</p>
        {onRetry ? (
          <Button onClick={onRetry} variant="outline">
            再試行
          </Button>
        ) : null}
      </div>
    )
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center"
    >
      <Loader2
        data-testid="auth-check-spinner"
        aria-hidden="true"
        className="h-8 w-8 animate-spin text-gray-400 motion-reduce:animate-none"
      />
      <p className="text-gray-600">Loading…</p>
      {isSlow ? (
        <p className="max-w-md text-sm text-gray-500">
          接続に時間がかかっています。そのままお待ちください。
        </p>
      ) : null}
    </div>
  )
}
